// Random events rolled each in-game hour (spec 10.1): phishing DMs, fake claim sites,
// collab offers (choices), plus protocol exploits, viral moments, and held-coin rugs.

import { TUNABLES } from "@degen/shared/config/tunables";
import { COLLAB_OFFERS, FAKE_CLAIMS, PHISHING_DMS, SCAM_LESSONS } from "@degen/shared/config/content";
import type { Rng } from "../rng";
import { pushLog, type GameState } from "../state";
import { addHeat, addTrust, playerClout } from "../player";
import { addShill, liveTokens } from "../market/market";
import { balanceOf, mcapOf } from "../market/token";
import { liveProtocols } from "../airdrops/protocols";
import { fillTemplate } from "../social/feed";
import type { ChoiceEvent } from "./sleuth";
import { money } from "@degen/shared/money";

const E = TUNABLES.events;

function queue(state: GameState, ev: Omit<ChoiceEvent, "id" | "tick">) {
  const ev2: ChoiceEvent = { ...ev, id: `ev${state.events.nextId++}`, tick: state.tick };
  state.events.pending.push(ev2);
  return ev2;
}

const SCAM_OPTIONS = [
  { id: "click", label: "Click the link" },
  { id: "ignore", label: "Ignore" },
  { id: "report", label: "Report" },
];

export function queuePhishing(state: GameState, rng: Rng, tutorial = false) {
  const dm = rng.pick(PHISHING_DMS);
  return queue(state, {
    kind: tutorial ? "tutorial_dm" : "phishing",
    title: "New DM",
    from: fillTemplate(dm.from, { handle: state.player.handle }),
    body: dm.body,
    options: SCAM_OPTIONS,
  });
}

function queueFakeClaim(state: GameState, rng: Rng) {
  const pr = liveProtocols(state);
  const name = pr.length ? rng.pick(pr).name : "LayerNull";
  const tpl = rng.pick(FAKE_CLAIMS);
  const vars = { protocol: name, slug: name.toLowerCase().replace(/[^a-z]/g, ""), ticker: name.slice(0, 4).toUpperCase() };
  queue(state, {
    kind: "fake_claim",
    title: "🪂 Airdrop claim",
    from: fillTemplate(tpl.from, vars),
    body: fillTemplate(tpl.body, vars),
    options: [
      { id: "click", label: "Connect & claim" },
      { id: "ignore", label: "Ignore" },
      { id: "report", label: "Report" },
    ],
  });
}

function queueCollab(state: GameState, rng: Rng) {
  const p = state.player;
  // Collab offers come from NPC coins; some of them are rugs waiting to happen.
  const candidates = liveTokens(state.market).filter((t) => t.creator.startsWith("npcdev") && !t.rugged && mcapOf(t) < 60_000);
  if (!candidates.length) return;
  const t = rng.pick(candidates);
  const scale = Math.min(1, Math.log10(p.followers / E.collabMinFollowers + 1));
  const pay = Math.round(E.collabPayUsd[0] + (E.collabPayUsd[1] - E.collabPayUsd[0]) * scale * rng.range(0.6, 1.2));
  queue(state, {
    kind: "collab",
    title: "🤝 Collab offer",
    from: `${t.ticker.toLowerCase()}_dev`,
    body: fillTemplate(rng.pick(COLLAB_OFFERS), { ticker: t.ticker, pay: money(pay) }),
    options: [
      { id: "accept", label: `Take the ${money(pay)}` },
      { id: "decline", label: "Decline" },
    ],
    tokenId: t.id,
    payUsd: pay,
  });
}

/** Hourly rolls. Choice events are skipped while asleep or when the queue is full. */
export function rollHourlyEvents(state: GameState, rng: Rng) {
  const p = state.player;
  const ev = state.events;
  const canAsk = !ev.asleep && ev.pending.length < E.maxPending && state.tick >= E.graceTicks;

  const penguin = state.pets.owned.some((x) => x.kind === "penguin" && x.happiness >= TUNABLES.pets.perkMinHappiness);
  const phishMult = penguin ? 1 - TUNABLES.pets.kinds.penguin.amount : 1;
  if (canAsk && rng.chance((E.phishingBasePerHour + E.phishingHeatPerHour * (p.heat / 100)) * phishMult)) queuePhishing(state, rng);
  else if (canAsk && rng.chance(E.fakeClaimPerHour)) queueFakeClaim(state, rng);
  else if (canAsk && p.followers >= E.collabMinFollowers && rng.chance(E.collabPerHour)) queueCollab(state, rng);

  // Protocol exploit: one farm protocol loses everyone's points.
  if (rng.chance(E.exploitPerHour)) {
    const live = liveProtocols(state);
    if (live.length) {
      const pr = rng.pick(live);
      const had = pr.playerPoints;
      pr.status = "exploited";
      pr.playerPoints = 0;
      pushLog(state, "exploit", `🚨 ${pr.name} got exploited. Points wiped.${had ? ` You lost ${Math.round(had)} points.` : ""}`, { protocol: pr.id, mine: had > 0 ? 1 : 0 });
    }
  }

  // Viral moment: your latest recent post gets x10 engagement.
  const clout = playerClout(p);
  if (rng.chance(E.viralBasePerHour + E.viralCloutPerHour * (clout / (clout + 5_000)))) {
    const feed = state.social.feed;
    for (let i = feed.length - 1; i >= 0; i--) {
      const post = feed[i];
      if (state.tick - post.tick > E.viralWindowTicks) break;
      if (post.author !== "player" || post.result?.includes("viral")) continue;
      const extra = post.likes * (E.viralMult - 1);
      post.likes += extra;
      post.reposts = Math.round(post.likes * 0.2);
      post.replies = Math.round(post.likes * 0.06);
      const gained = Math.round(extra * TUNABLES.social.conversion * (p.trust / 100));
      p.followers += gained;
      post.result = `${post.result ? post.result + " · " : ""}🚀 Went viral · +${gained} followers`;
      pushLog(state, "viral", `🚀 Your post went viral! +${gained.toLocaleString()} followers.`);
      break;
    }
  }

  // Collab deals: shilling a coin that rugs comes back to bite you.
  ev.collabs = ev.collabs.filter((d) => {
    const t = state.market.tokens[d.tokenId];
    if (t?.rugged) {
      addTrust(p, E.collabRugTrust);
      pushLog(state, "collab_rug", `The coin you were paid to shill ($${t.ticker}) rugged. Followers blame you. Trust down.`, { tokenId: t.id });
      return false;
    }
    return state.tick < d.untilTick;
  });
}

/** Track "rugs survived": a coin you hold rugs. */
export function noteHeldRug(state: GameState, tokenId: string) {
  const t = state.market.tokens[tokenId];
  if (!t || t.creator === "player" || balanceOf(t, "player") <= 0) return;
  state.player.stats.rugsSurvived++;
  pushLog(state, "held_rug", `💀 $${t.ticker} rugged while you were holding. Sell what's left or hold the memory.`, { tokenId });
}

export type ResolveResult = { ok: true; data?: Record<string, number> } | { ok: false; error: string };

export function resolveEvent(state: GameState, rng: Rng, eventId: string, choice: string): ResolveResult {
  const ev = state.events;
  const e = ev.pending.find((x) => x.id === eventId);
  if (!e) return { ok: false, error: "That popup is gone." };
  if (!e.options.some((o) => o.id === choice)) return { ok: false, error: "Pick an option." };
  ev.pending = ev.pending.filter((x) => x !== e);
  const p = state.player;
  const lesson = rng.pick(SCAM_LESSONS);

  if (e.kind === "phishing" || e.kind === "fake_claim" || e.kind === "tutorial_dm") {
    if (choice === "click") {
      if (e.kind === "tutorial_dm") {
        pushLog(state, "scam_lesson", `That was a scam. This time it cost nothing (tutorial). Normally it drains 30-100% of your cash. ${lesson}`, { tutorial: 1 });
        return { ok: true, data: { lost: 0 } };
      }
      const lost = Math.round(p.cash * rng.range(E.phishLoss[0], E.phishLoss[1]) * 100) / 100;
      p.cash -= lost;
      addHeat(p, E.phishHeat);
      pushLog(state, "drained", `🪝 Drained. The link emptied ${money(Math.round(lost))} from your wallet. Tokens are safe. ${lesson}`, { lost });
      return { ok: true, data: { lost } };
    }
    if (choice === "report") {
      addTrust(p, E.reportTrust);
      pushLog(state, "reported", "Reported. The timeline thanks you. Trust up.");
    } else pushLog(state, "ignored", "Ignored. Correct.");
    return { ok: true, data: { lost: 0 } };
  }

  if (e.kind === "collab") {
    if (choice === "accept" && e.tokenId && e.payUsd) {
      const t = state.market.tokens[e.tokenId];
      p.cash += e.payUsd;
      if (t && t.phase !== "dead") {
        addShill(state.market, { tokenId: t.id, by: "player", tick: state.tick, strength: 1 });
        state.social.feed.push({
          id: `p${state.social.nextPostId++}`,
          tick: state.tick,
          author: "player",
          type: "shill",
          text: `excited to support $${t.ticker} 🤝 big things coming`,
          tokenId: t.id,
          likes: Math.round(Math.pow(playerClout(p) + 40, 0.6)),
          reposts: 0,
          replies: 0,
          replyTexts: [],
          result: "Paid partnership",
        });
        ev.collabs.push({ tokenId: t.id, untilTick: state.tick + E.collabRugWindowTicks });
      }
      pushLog(state, "collab", `You took ${money(e.payUsd)} to shill $${t?.ticker}. Hope it doesn't rug.`, { tokenId: e.tokenId });
      return { ok: true, data: { pay: e.payUsd } };
    }
    pushLog(state, "ignored", "Declined the collab.");
    return { ok: true };
  }
  return { ok: true };
}
